import React from 'react';
import { AlertProps, AlertTone } from '../../../types/DesignSystem/Alert';
import { Cross } from '../Iconography/Icons';
import { TONE_GLYPH } from '../toneGlyph';
import Typography from '../Typography/Typography';
import { FOCUS_RING } from '../focusRing';
import { withClassName } from '../cn';

/**
 * Tone → tint ground and glyph ink (§ 63 — the ADS SectionMessage grammar).
 *
 * **The tints are `Status`'s pairs, deliberately.** A chip and a banner reporting the
 * same outcome must be the same colour. `neutral` differs — `Status` uses `grey-200`,
 * this uses the sunken-surface ground: a chip is a token inside a row, a banner IS a
 * surface.
 *
 * **The tone ink now colours ONLY the glyph.** Title and body are the page's own
 * neutral inks: measured, `ink` is 10.18–13.09 on the light tints and 6.81–8.95 on
 * the dark ones. Five differently-coloured headlines made every banner shout; one
 * voice with a coloured mark is the SectionMessage lesson.
 *
 * **And no border, of any colour.** The § 51-era `grey-200` hairline existed because
 * a TONE border could not (`blue` is staged — no blue-200). § 63 drops it entirely:
 * every tint separates from both page grounds on both themes, so a hairline on top
 * of the tint is chrome with no job.
 */
const toneStyles: Record<AlertTone, { surface: string; glyph: string }> = {
  neutral: { surface: 'tw-bg-surface-sunken', glyph: 'tw-text-ink' },
  info: { surface: 'tw-bg-tone-info-tint', glyph: 'tw-text-tone-info-ink' },
  success: { surface: 'tw-bg-tone-success-tint', glyph: 'tw-text-tone-success-ink' },
  warning: { surface: 'tw-bg-tone-warning-tint', glyph: 'tw-text-tone-warning-ink' },
  danger: { surface: 'tw-bg-tone-danger-tint', glyph: 'tw-text-tone-danger-ink' },
};

/**
 * SC 1.4.1 (§ 63): with the title ink neutral, colour + icon are the only tone
 * carriers — and the icon is aria-hidden. Each non-neutral tone therefore prefixes
 * an sr-only word, so "which kind of message" survives a screen reader. `danger`
 * says "Error" — the word users know; "danger" is a palette name.
 */
const TONE_WORD: Record<AlertTone, string | null> = {
  neutral: null,
  info: 'Information',
  success: 'Success',
  warning: 'Warning',
  danger: 'Error',
};

const URGENT: AlertTone[] = ['warning', 'danger'];

/** The accent link grammar (§ 63) — same duty accent carries everywhere else.
    § 295 — and the same hover as `Link`: ink to `accent-hover` on a fade. The
    underline alone cannot tween, so an action that only underlined snapped. */
const ACTION_LINK =
  `tw-text-title tw-text-accent hover:tw-text-accent-hover hover:tw-underline tw-rounded-ctl motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING}`;

/**
 * Inline banner — a message that belongs to the page, where `Toast` is a message that
 * passes over it.
 *
 * **Body text is `ink-secondary` (grey-700), not `grey-600`, and that is a measurement
 * not a taste.** `grey-600` is the documented tertiary-text floor at 4.52:1 — *on
 * white*. On the five tint grounds this component uses it measures **3.75–4.19:1** and
 * fails AA on every one of them. A contrast figure without its ground named is not a
 * contrast figure.
 */
const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  const {
    tone = 'neutral',
    title,
    children,
    onDismiss,
    dismissLabel = 'Dismiss',
    actions,
    live = false,
    icon,
    className = '',
    'data-testid': dataTestId,
    role,
    ...rest
  } = props;

  /*
    `'role' in props`, not a default parameter.

    `role = 'status'` would fire whenever a caller passed `role={undefined}` — so a
    documented opt-out would silently not exist. That exact bug shipped in
    `ErrorState` and, before it, in a test helper's `channelKey` (recorded in
    CLAUDE.md). An explicit caller role always wins here, including `role="none"`.
  */
  const resolvedRole = 'role' in props
    ? role
    : (live ? (URGENT.includes(tone) ? 'alert' : 'status') : undefined);

  const { surface, glyph } = toneStyles[tone];
  const Glyph = TONE_GLYPH[tone];
  const word = TONE_WORD[tone];
  const testId = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

  return (
    <div
      {...rest}
      ref={ref}
      role={resolvedRole}
      className={withClassName(`tw-flex tw-items-start tw-gap-3 tw-p-4 tw-rounded-srf ${surface}`, className)}
      data-tone={tone}
      data-testid={dataTestId}
    >
      {/* § 328 — `icon` undefined keeps the tone glyph; `null` drops the mark; any
          other node takes its place. `'icon' in props`, for the same reason as `role`
          above: `icon={undefined}` must mean "default", not "none". */}
      {!('icon' in props) || icon === undefined ? (
        <Glyph size={20} aria-hidden="true" className={`tw-shrink-0 ${glyph}`} data-testid={testId('icon')} />
      ) : icon === null ? null : (
        <span className="tw-shrink-0 tw-inline-flex" aria-hidden="true" data-testid={testId('icon')}>{icon}</span>
      )}

      <div className="tw-flex-1 tw-min-w-0">
        {word && <span className="tw-sr-only">{word}: </span>}
        {title && (
          <Typography variant="title-header" className="tw-text-ink" data-testid={testId('title')}>
            {title}
          </Typography>
        )}
        {children && (
          <div
            className={`tw-text-body-primary tw-text-ink-secondary ${title ? 'tw-mt-1' : ''}`}
            data-testid={testId('body')}
          >
            {children}
          </div>
        )}
        {actions && actions.length > 0 && (
          /* Not auto-dismissing, unlike `Toast`'s action. A banner belongs to the
             page: the condition it reports is usually still true after the action
             starts, and removing it would hide the thing being fixed. */
          <ul
            className={`tw-flex tw-flex-wrap tw-items-center tw-gap-2 ${title || children ? 'tw-mt-2' : ''}`}
            data-testid={testId('actions')}
          >
            {actions.map((action, index) => (
              // Labels are the only identity an action has; position disambiguates.
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${action.label}-${index}`} className="tw-flex tw-items-center tw-gap-2">
                {/* Middot OUTSIDE the link, aria-hidden — inside, it would join the
                    accessible name; the list structure already separates. */}
                {index > 0 && <span aria-hidden="true" className="tw-text-ink-tertiary">·</span>}
                {action.href ? (
                  <a
                    href={action.href}
                    onClick={action.onClick}
                    className={ACTION_LINK}
                    data-testid={action['data-testid']}
                  >
                    {action.label}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={action.onClick}
                    className={ACTION_LINK}
                    data-testid={action['data-testid']}
                  >
                    {action.label}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          /* 24px box round a 16px glyph — SC 2.5.8. Same sizing as Toast's. */
          className={`tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-h-6 tw-w-6 tw-rounded-ctl tw-text-ink-secondary motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift hover:tw-text-ink ${FOCUS_RING}`}
          aria-label={dismissLabel}
          data-testid={testId('dismiss')}
        >
          <Cross size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
});

export default Alert;
